/**
 * Fetch wrapper for the backend API.
 *  - Same-origin requests with the httpOnly session cookies.
 *  - Adds the CSRF header the API requires on state-changing requests.
 *  - On 401 TOKEN_EXPIRED: refreshes the session once (single flight) and
 *    retries; if the refresh fails the user is sent back to IDHub. It never
 *    retries in a loop (IDHub integration checklist).
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

const RETURN_TO_KEY = 'apr.returnTo';
let refreshInFlight: Promise<boolean> | null = null;

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    return new ApiError(
      response.status,
      body.error?.code ?? 'HTTP_ERROR',
      body.error?.message ?? response.statusText,
      body.error?.details,
    );
  } catch {
    return new ApiError(response.status, 'HTTP_ERROR', response.statusText);
  }
}

function send(path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  headers.set('X-Requested-With', 'fetch');
  if (init.body && !(init.body instanceof FormData)) headers.set('Content-Type', 'application/json');
  return fetch(path, { ...init, headers, credentials: 'same-origin' });
}

/** Refreshes the access token through the backend. Shared by concurrent callers. */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= send('/api/auth/refresh', { method: 'POST' })
    .then((r) => r.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

export async function redirectToLogin(): Promise<never> {
  sessionStorage.setItem(RETURN_TO_KEY, window.location.pathname + window.location.search);
  const response = await fetch('/api/auth/config');
  const { loginUrl } = (await response.json()) as { loginUrl: string };
  window.location.assign(loginUrl);
  return new Promise<never>(() => {}); // the page is navigating away
}

export function consumeReturnTo(): string {
  const value = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return value && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/auth/') ? value : '/';
}

export async function apiRequest(path: string, init: RequestInit = {}): Promise<Response> {
  let response = await send(path, init);
  if (response.status === 401) {
    const error = await parseError(response.clone());
    if (error.code === 'TOKEN_EXPIRED' && (await refreshSession())) {
      response = await send(path, init);
    }
    if (response.status === 401) return redirectToLogin();
  }
  if (!response.ok) {
    const error = await parseError(response);
    // Access pending or withdrawn by an administrator: reloading shows the
    // matching screen (the session itself stays valid, so there is no loop).
    if (error.code === 'ACCESS_PENDING' || error.code === 'ACCESS_DENIED') {
      window.location.reload();
      return new Promise<never>(() => {});
    }
    throw error;
  }
  return response;
}

export async function apiJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await apiRequest(path, init);
  return (response.status === 204 ? undefined : await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => apiJson<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiJson<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => apiJson<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => apiJson<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
};

/** Raw call used during sign-in (no refresh/redirect handling). */
export function sendRaw(path: string, init: RequestInit = {}): Promise<Response> {
  return send(path, init);
}
